import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, Lock, Mail } from "lucide-react";
import { applicantApi, type ApiError, type SignalSummary } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { IS_STAGING } from "@/lib/env";

/**
 * Email-OTP panel. Used by both the applicant flow (Page 2) and the voucher
 * flow (Step 1) — the verify-an-email mechanic is identical for both.
 *
 * Persists in-flight state in sessionStorage so a user who sends a code,
 * navigates away (or reloads), and returns can still enter the code. On
 * successful verify the persisted state is cleared.
 *
 * `verifiedSignals` is an opaque list of already-verified emails to render as
 * green confirmation chips above the input form. Pass [] (or omit) for none.
 */
export function EmailCodePanel({
  token,
  onVerified,
  verifiedSignals,
  verifiedDisplay = "tiles",
  lockedEmail,
  initialEmail,
}: {
  token: string;
  onVerified?: () => void | Promise<void>;
  verifiedSignals?: SignalSummary[];
  /**
   * How to render already-verified emails:
   * - "tiles" (default): heavy green confirmation tiles (legacy / voucher flow).
   * - "inline": subtle muted single-line list — used by the applicant flow
   *   where the Google provider row carries its own "Connected" badge and a
   *   second heavy green block would be redundant.
   * - "none": don't render verified emails at all.
   */
  verifiedDisplay?: "tiles" | "inline" | "none";
  /**
   * When provided, the panel enters "pre-locked" mode: the email field is
   * pre-filled and read-only, the header reads "Verify your application
   * email", and the primary CTA reads "Send Verification Code to {email}".
   * Once that exact address verifies, the panel collapses to a single green
   * confirmation chip and does NOT offer "Verify another email" (that
   * affordance lives elsewhere in scenario B of the email step).
   */
  lockedEmail?: string;
  /**
   * Pre-fills the email field and leaves it editable. For the applicant's own
   * application address, which the applicant should not have to retype to
   * confirm. A code already in flight (restored from sessionStorage) wins.
   */
  initialEmail?: string;
}) {
  const storageKey = `dv_email_pending_${token}`;
  // `emailId` used to live here too; the backend looks the row up by
  // (session, email) and nothing ever read it back. Older persisted entries
  // carry the extra field — JSON.parse ignores it.
  type Pending = { email: string; sent: boolean; stagingCode: string | null };
  const loadPending = (): Pending | null => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Pending) : null;
    } catch { return null; }
  };
  // Lazy initializers: this reads + JSON.parses sessionStorage, and the code
  // input re-renders the panel on every keystroke.
  const [email, setEmail] = useState(() => lockedEmail ?? loadPending()?.email ?? initialEmail ?? "");
  const [code, setCode] = useState("");
  const [stagingCode, setStagingCode] = useState<string | null>(() => loadPending()?.stagingCode ?? null);
  const [sent, setSent] = useState(() => loadPending()?.sent ?? false);
  const [busy, setBusy] = useState(false);
  // If a pending code-entry was restored, default to "adding another" so the
  // form is visible even when other emails have already been verified.
  const [addingAnother, setAddingAnother] = useState(() => Boolean(loadPending()?.sent));

  const isStaging = IS_STAGING;
  const verified = verifiedSignals ?? [];
  const hasVerified = verified.length > 0;

  const persistPending = (p: Pending | null) => {
    try {
      if (p) sessionStorage.setItem(storageKey, JSON.stringify(p));
      else sessionStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  };

  /** Tear down the in-flight code so the panel offers "Send code" again,
   *  KEEPING the address — the recovery every "request a new code" error
   *  needs. `reset()` is this plus clearing the address. Keep them composed:
   *  two hand-maintained lists of "what is pending state" drift. */
  const resetToSendState = () => {
    setCode("");
    setStagingCode(null);
    setSent(false);
    persistPending(null);
  };

  const reset = () => {
    resetToSendState();
    setEmail("");
  };

  const send = async () => {
    setBusy(true);
    try {
      const res = await applicantApi.sendEmailCode(token, email);
      setSent(true);
      const sCode = isStaging && res.code ? res.code : null;
      setStagingCode(sCode);
      if (sCode) setCode(sCode);
      persistPending({ email, sent: true, stagingCode: sCode });
      toast({ title: "Code sent", description: `We emailed a 6-digit code to ${email}.` });
    } catch (e) {
      toast({ title: "Could not send code", description: (e as ApiError).message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  /** Errors whose only recovery is a brand-new code (verified as of
   *  2026-07-09, `email-verification.ts:287-311`): 429 the attempt budget is
   *  spent — the row is locked and even the CORRECT code is refused; 410 the
   *  code expired; 404 no pending row for this address (the code was already
   *  consumed, so a replay lands here rather than on the 409 the legacy
   *  email_id lookup returns). All three used to fall through to a generic
   *  "Invalid code" that left the panel showing only a Verify button the
   *  backend would never accept again — a dead end. */
  const NEEDS_NEW_CODE: Record<number, string> = {
    429: "Too many attempts",
    410: "That code expired",
    404: "That code is no longer valid",
  };

  const confirm = async () => {
    if (!email || !code) return;
    setBusy(true);
    let succeeded = false;
    try {
      const result = await applicantApi.confirmEmailCode(token, email, code);
      // A wrong code is a 200 with `verified: false`, NOT an exception — see
      // applicantApi.confirmEmailCode. Treating any 2xx as success told the
      // applicant their email was verified when it wasn't.
      if (result?.verified) {
        succeeded = true;
      } else {
        // Keep the mistyped code in the field so the user can see and correct
        // it. Clearing it here is also unsafe: this runs in an async
        // continuation, so on a slow response it wipes whatever the user has
        // typed for their NEXT attempt in the meantime — and with the button
        // gated on code.length, that stranded it disabled (the J5 CI flake,
        // runs 29159892531/29692070709: two clean 200s on the wire, deadlock
        // in the panel).
        toast({
          title: "That code didn't match",
          description: "Check the code and try again, or send a new one.",
          variant: "destructive",
        });
      }
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 409) {
        // Already verified — the goal state. Nothing to retry.
        succeeded = true;
      } else if (err.status !== undefined && NEEDS_NEW_CODE[err.status]) {
        toast({
          title: NEEDS_NEW_CODE[err.status],
          description: "Send a new code to try again.",
          variant: "destructive",
        });
        resetToSendState();
      } else {
        // Everything reaching here is a transport/server fault, never a bad
        // code — so it must not be labelled one.
        toast({
          title: "Couldn't verify that code",
          description: err.message,
          variant: "destructive",
        });
      }
    } finally { setBusy(false); }

    // The success side effects run OUTSIDE the try: a rejecting `onVerified`
    // must not be mistaken for a verification failure, which would toast
    // "Invalid code" over "Email verified" — and, if it rejected with a 410
    // (an expired session poll), would drop the panel back to its send state
    // after the email had already been accepted.
    if (!succeeded) return;
    toast({ title: "Email verified", description: "Continuing to the next step." });
    reset();
    setAddingAnother(false);
    try {
      await onVerified?.();
    } catch {
      // The callback (a session refresh) owns its own errors. A failed refresh
      // must neither undo a completed verification nor surface as one.
    }
  };

  // Locked-email mode: panel is dedicated to verifying a single
  // pre-known address (the applicant's application email). Behavior diverges:
  // verified state collapses into a single chip, the form is always shown
  // until verified, and "Verify another email" is suppressed.
  const isLocked = Boolean(lockedEmail);
  const lockedVerified = isLocked
    && verified.some((s) => (s.detail ?? "").toLowerCase() === lockedEmail!.toLowerCase());

  const showForm = isLocked ? !lockedVerified : (!hasVerified || addingAnother || sent);

  if (isLocked && lockedVerified) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-trust-green/40 bg-trust-green-muted/40 px-3 py-2.5">
        <CheckCircle2 className="w-4 h-4 text-trust-green flex-shrink-0" />
        <p className="text-sm font-medium text-foreground truncate">
          <span className="font-mono">{lockedEmail}</span> verified
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Verified emails — rendering depends on `verifiedDisplay`. Suppressed entirely in locked mode (chip above handles it). */}
      {!isLocked && verifiedDisplay === "tiles" && verified.map((sig, i) => (
        <div
          key={`${sig.key}-${i}`}
          className="border border-trust-green/40 bg-trust-green-muted/40 rounded-lg p-3"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-trust-green" />
            <p className="text-sm font-medium text-foreground">Email verified</p>
          </div>
          {sig.detail && (
            <p className="text-xs text-muted-foreground mt-1 ml-6 font-mono">
              {sig.detail}
            </p>
          )}
        </div>
      ))}

      {!isLocked && verifiedDisplay === "inline" && hasVerified && (
        <p className="text-xs text-muted-foreground px-1">
          Verified:{" "}
          <span className="font-mono text-foreground/80">
            {verified.map((s) => s.detail ?? s.key).filter(Boolean).join(", ")}
          </span>
        </p>
      )}

      {/* "Add another" CTA when verified and not currently adding — not shown in locked mode */}
      {!isLocked && hasVerified && !addingAnother && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setAddingAnother(true)}
        >
          + Verify another email (optional)
        </Button>
      )}

      {/* Input form */}
      {showForm && (
        <div className={isLocked ? "space-y-3" : "border border-border rounded-lg p-3 space-y-3"}>
          {!isLocked && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-trust-blue" />
                <p className="text-sm font-medium text-foreground">
                  {hasVerified ? "Verify another email" : "Or verify with an email code"}
                </p>
              </div>
              {hasVerified && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-muted-foreground"
                  onClick={() => { reset(); setAddingAnother(false); }}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
          {isLocked ? (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
              <span className="text-sm font-medium text-foreground truncate flex-1 font-mono">
                {lockedEmail}
              </span>
              <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </div>
          ) : (
            <Input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
          {sent && (
            <Input
              type="text" placeholder="000000" maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="text-center tracking-[0.5em] font-mono"
            />
          )}
          {sent && stagingCode && (
            <div className="text-xs rounded-md border border-amber-300/60 bg-amber-50 text-amber-900 px-2 py-1.5 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-700/40">
              Dev mode — your verification code is <span className="font-mono font-semibold">{stagingCode}</span>
            </div>
          )}
          {!sent ? (
            <Button size={isLocked ? "default" : "sm"} className="w-full" onClick={send} disabled={busy || !email.includes("@")}>
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {isLocked ? `Send verification code to ${lockedEmail}` : "Send code"}
            </Button>
          ) : (
            <>
              <Button size={isLocked ? "default" : "sm"} className="w-full" onClick={confirm} disabled={busy || code.length < 6}>
                {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Verify code
              </Button>
              {/* The wrong-code toast tells the applicant they can "send a new
                  one" — this is that affordance. Without it the only control
                  in the sent state was Verify, so a mistyped, expired, or
                  locked-out code had no way forward. */}
              <button
                type="button"
                onClick={send}
                disabled={busy}
                className="block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                Send a new code
              </button>
            </>
          )}
          {isLocked && !sent && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              We'll send a secure 6-digit code to this address to verify active ownership. No password required.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
